/**
 * Cuts a streaming answer's markdown into pieces ready to be sent to
 * `/api/agent/speech` while it is still being written (ported from the
 * MEIS-13490 prototype's `agentSpeechPieces.ts` — this module only decides
 * where to cut, which does not depend on the wire format used to actually
 * request/play a piece). A piece is only emitted once it ends on a
 * sentence (or paragraph) boundary and is not left holding an unclosed
 * code block or table open — the server turns tables/code into pointer
 * sentences, which only works once the block is complete.
 */

/** Emit a piece once complete sentences reach roughly this many characters. */
const MIN_PIECE_LENGTH = 150;

/**
 * A paragraph that is only a list number, a dash or a colon is not a piece
 * of its own: sent alone, the voice model had nothing to read and
 * improvised a monologue instead. It joins the next paragraph.
 */
function hasWords(text: string): boolean {
  return (text.match(/\p{L}/gu) ?? []).length >= 3;
}
/** Never wait longer than this for a boundary (a single very long sentence). */
const HARD_MAX_LENGTH = 600;

/**
 * The index where an unmatched, still-open code fence (```) starts in `text`,
 * or null when every fence in `text` is closed.
 */
function openCodeFenceStart(text: string): number | null {
  const fenceRe = /```/g;
  let match: RegExpExecArray | null;
  let openAt: number | null = null;
  while ((match = fenceRe.exec(text))) {
    openAt = openAt === null ? match.index : null;
  }
  return openAt;
}

/**
 * The index where a table run still open at the end of `text` starts (a run
 * of `| ... |` lines not yet followed by a blank line), or null when the text
 * does not currently end inside a table.
 */
function openTableStart(text: string): number | null {
  const lines: Array<{ start: number; text: string }> = [];
  let offset = 0;
  for (const line of text.split("\n")) {
    lines.push({ start: offset, text: line });
    offset += line.length + 1;
  }
  let i = lines.length - 1;
  while (i >= 0 && lines[i].text.trim() === "") i--;
  if (i < 0 || !/^\s*\|/.test(lines[i].text)) return null;
  let start = lines[i].start;
  let j = i;
  while (j >= 0 && /^\s*\|/.test(lines[j].text)) {
    start = lines[j].start;
    j--;
  }
  return start;
}

/** Boundary candidates in `segment`: right after a paragraph break or a sentence end. */
function boundaryCandidates(
  segment: string
): Array<{ pos: number; paragraph: boolean }> {
  const candidates: Array<{ pos: number; paragraph: boolean }> = [];
  const paraRe = /\n[ \t]*\n/g;
  let m: RegExpExecArray | null;
  while ((m = paraRe.exec(segment)))
    candidates.push({ pos: m.index + m[0].length, paragraph: true });
  const sentRe = /[.!?]+(?=\s|$)/g;
  while ((m = sentRe.exec(segment)))
    candidates.push({ pos: m.index + m[0].length, paragraph: false });
  return candidates.sort((a, b) => a.pos - b.pos);
}

/**
 * The next piece ready to speak from `pending` (the not-yet-sent tail of the
 * message), and the rest that stays pending. Empty piece: nothing is ready
 * yet. `final`: the answer is complete, so everything is flushed regardless
 * of length or open blocks.
 */
function nextSpeechPiece(
  pending: string,
  final: boolean
): { piece: string; rest: string } {
  if (final) {
    const piece = pending.trim();
    return piece ? { piece, rest: "" } : { piece: "", rest: pending };
  }

  const limit = Math.min(
    openCodeFenceStart(pending) ?? Infinity,
    openTableStart(pending) ?? Infinity,
    pending.length
  );
  const safe = pending.slice(0, limit);

  let cut = -1;
  for (const candidate of boundaryCandidates(safe)) {
    if (
      (candidate.paragraph && hasWords(safe.slice(0, candidate.pos))) ||
      candidate.pos >= MIN_PIECE_LENGTH
    ) {
      cut = candidate.pos;
      break;
    }
  }
  if (cut < 0 && safe.length >= HARD_MAX_LENGTH) {
    // A single overlong sentence (or an open block held back for too long):
    // cut at the last space within the hard limit so speaking is not stalled
    // forever waiting for punctuation or a closing fence/table.
    const at = safe.lastIndexOf(" ", HARD_MAX_LENGTH);
    cut = at > 0 ? at : HARD_MAX_LENGTH;
  }
  if (cut < 0) return { piece: "", rest: pending };
  return { piece: pending.slice(0, cut).trim(), rest: pending.slice(cut) };
}

export type SpeechPieceResult = {
  /** Newly ready pieces, in order, cut at sentence/paragraph boundaries. */
  pieces: string[];
  /** How much of `text` (from its start) these pieces consumed. */
  consumed: number;
};

/** One ready piece and where it ends in the text it was cut from. */
export type SpeechPieceCut = {
  piece: string;
  /** Offset in `text` right after this piece (the next piece starts from here). */
  end: number;
};

/**
 * The pieces ready to speak from `text` starting at `consumed`, each with the
 * offset where it ends — so a caller can keep pieces it already requested
 * when the text changes underneath them and restart right after the last one
 * still valid. `final`: the rest is flushed in one last piece.
 */
export function splitSpeechPieces(
  text: string,
  consumed: number,
  final: boolean
): SpeechPieceCut[] {
  let offset = consumed;
  const cuts: SpeechPieceCut[] = [];
  for (;;) {
    const pending = text.slice(offset);
    const { piece, rest } = nextSpeechPiece(pending, final);
    if (!piece) break;
    offset = text.length - rest.length;
    cuts.push({ piece, end: offset });
    if (final) break;
  }
  return cuts;
}

/**
 * Turns the growth of a streaming answer into speakable pieces. `text` is
 * the full visible text so far (from the message's start); `consumed` is how
 * much of it was already turned into pieces by an earlier call. `final`:
 * the answer finished, so the rest is flushed in one last piece.
 */
export function extractSpeechPieces(
  text: string,
  consumed: number,
  final: boolean
): SpeechPieceResult {
  const cuts = splitSpeechPieces(text, consumed, final);
  return {
    pieces: cuts.map(cut => cut.piece),
    consumed: cuts[cuts.length - 1]?.end ?? consumed,
  };
}

/**
 * A piece reduced to what the voice actually says, for telling whether an
 * already requested piece still matches the text shown after it changed (the
 * finished answer replacing its streamed draft adds links, but reads the
 * same): link and image targets, emphasis/heading/list/quote markup and
 * extra whitespace are left out. Only used for comparing — the backend's
 * `speakableText` does the actual conversion for the voice.
 */
export function speechComparableText(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[`*_~#>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Characters `speechComparableText` drops (emphasis, headings, quotes, tables, code). */
const SPEECH_MARKUP = new Set(["`", "*", "_", "~", "#", ">", "|"]);

/**
 * `text` reduced the way `speechComparableText` reduces it — image dropped,
 * link collapsed to its label, markup characters dropped, whitespace runs
 * collapsed to one space — alongside a map back to raw positions:
 * `rawEnd[i]` is the raw index in `text` right after the raw content that
 * produced `comparable[i]`, so a length in the comparable string maps back
 * to where reading continues in `text`.
 */
function comparableWithRawEnds(text: string): {
  comparable: string;
  rawEnd: number[];
} {
  const out: string[] = [];
  const rawEnd: number[] = [];
  const pushSpace = (end: number) => {
    if (out.length === 0) return;
    if (out[out.length - 1] === " ") rawEnd[rawEnd.length - 1] = end;
    else {
      out.push(" ");
      rawEnd.push(end);
    }
  };
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    const isImage = ch === "!" && text[i + 1] === "[";
    if (ch === "[" || isImage) {
      const open = isImage ? i + 1 : i;
      const close = text.indexOf("]", open + 1);
      const closeParen =
        close >= 0 && text[close + 1] === "("
          ? text.indexOf(")", close + 2)
          : -1;
      // `speechComparableText` needs a non-empty label for a link.
      if (closeParen >= 0 && (isImage || close > open + 1)) {
        if (!isImage) {
          const label = comparableWithRawEnds(text.slice(open + 1, close));
          for (let j = 0; j < label.comparable.length; j++) {
            const end = open + 1 + label.rawEnd[j];
            if (label.comparable[j] === " ") pushSpace(end);
            else {
              out.push(label.comparable[j]);
              rawEnd.push(end);
            }
          }
        }
        i = closeParen + 1;
        continue;
      }
    }
    if (SPEECH_MARKUP.has(ch)) {
      i += 1;
      continue;
    }
    if (/\s/.test(ch)) {
      let end = i;
      while (end < text.length && /\s/.test(text[end])) end += 1;
      pushSpace(end);
      i = end;
      continue;
    }
    out.push(ch);
    rawEnd.push(i + 1);
    i += 1;
  }
  return { comparable: out.join(""), rawEnd };
}

/**
 * How many of the already requested `pieces` (in order) the shown `text`
 * still reads the same (`speechComparableText`), and where in `text`
 * reading continues after them. Walks the pieces along `text` itself rather
 * than cutting `text` afresh: the finished reply replacing the streamed one
 * can add a link or turn a space into a paragraph break inside what was
 * already read, which moves where a fresh cut would fall without changing a
 * word — comparing fresh cuts then dropped every piece and read the answer
 * again from the top. Only a changed wording (or a piece the new text
 * continues mid-word) stops the walk.
 */
export function keptSpeechPieces(
  pieces: readonly string[],
  text: string
): { kept: number; consumed: number } {
  const { comparable, rawEnd } = comparableWithRawEnds(text);
  let position = 0;
  let kept = 0;
  let consumed = 0;
  for (const piece of pieces) {
    const spoken = speechComparableText(piece);
    const start = comparable[position] === " " ? position + 1 : position;
    const end = start + spoken.length;
    if (
      spoken === "" ||
      !comparable.startsWith(spoken, start) ||
      (end < comparable.length && comparable[end] !== " ")
    )
      break;
    position = end;
    kept += 1;
    consumed = rawEnd[end - 1] ?? consumed;
  }
  return { kept, consumed };
}
